import { Save } from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';
import React, { useEffect, useState } from 'react';

import CAccountDeleteForm from '../../components/CAccountDeleteForm';
import CButton from '../../components/CButton';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CustomTextField from '../../components/CustomTextField';
import PageModuleFormGroup from '../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../components/PageModuleFormRow';
import Page from '../../components/layout/Page';
import { EUserRoles } from '../../enums/EUserRoles';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import type { IRequestState } from '../../models/IRequestState';
import SvUserManagementCoreService from '../../services/SvUserManagementCoreService';
import UserManagementService from '../../services/userManagementService';
import { updateSnackbar } from '../../store/appSlice';
import { updateUserObject } from '../../store/userSlice';

interface Form {
  firstName: string;
  lastName: string;
  email: string;
  roles: EUserRoles[];
}

interface State {
  reqState: IRequestState;
  form: Partial<Form>;
}

const USER_ROLE_VALUES = Object.values(EUserRoles);

function getRoleLabel(role: EUserRoles): string {
  return role.replace('nexdos-app-', '').split('-').join(' ');
}

function areRolesEqual(first: EUserRoles[], second: EUserRoles[]): boolean {
  return first.length === second.length && first.every((role) => second.includes(role));
}

export default function UserSecurityAndPrivacy() {
  const dispatch = useAppDispatch();
  const accountManagementText = CLocalizedTextStringStatic('selfServiceAccount.page.title');
  const firstNameLabel = CLocalizedTextStringStatic('textField.label.firstName');
  const lastNameLabel = CLocalizedTextStringStatic('textField.label.lastName');
  const emailLabel = CLocalizedTextStringStatic('textField.label.email');

  const { id, email, firstName, lastName, roles } = useAppSelector((state) => state.user);

  const initialForm = {
    firstName: firstName?.trim() ?? '',
    lastName: lastName?.trim() ?? '',
    email: email?.trim() ?? '',
    roles
  };

  const [state, setState] = useState<State>({
    reqState: { isLoading: true, isError: false },
    form: initialForm
  });

  useEffect(() => {
    if (!id) return;

    const fetchUserDetails = async () => {
      const res = await SvUserManagementCoreService.getUserDetails(id);
      const isSuccess = await res.isSuccess();

      if (!isSuccess) {
        updateStateAttribute<State>(
          {
            reqState: {
              isLoading: false,
              isError: true,
              strError: await res.getMessage(),
              objError: await res.getDetail()
            }
          },
          setState
        );
        return;
      }

      const user = await res.getData();

      updateStateAttribute<State>(
        {
          form: {
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            email: user?.email ?? '',
            roles: user?.roles ?? []
          },
          reqState: {
            isLoading: false,
            isError: false
          }
        },
        setState
      );

      dispatch(
        updateUserObject({
          id,
          email: user?.email ?? '',
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          roles: user?.roles ?? [],
          isAdmin: user?.roles?.includes(EUserRoles.ADMIN) ?? false,
          isStaff: user?.roles?.includes(EUserRoles.STAFF) ?? false,
          isPortfolioManager: user?.roles?.includes(EUserRoles.PORTFOLIO_MANAGER) ?? false,
          isUser: (user?.roles?.length ?? 0) === 0
        })
      );
    };

    void fetchUserDetails();
  }, [dispatch, id]);

  const handleRoleChange = (role: EUserRoles, checked: boolean) => {
    let currentRoles = [...(state.form.roles ?? [])];

    if (checked) {
      /**
       * TODO: Remove in the next iteration. Let the associated roles be handled by the BE.
       */
      if (!currentRoles.includes(role)) {
        currentRoles.push(role);
      }

      if (
        (role === EUserRoles.ADMIN || role === EUserRoles.PORTFOLIO_MANAGER) &&
        !currentRoles.includes(EUserRoles.STAFF)
      ) {
        currentRoles.push(EUserRoles.STAFF);
      }
    } else {
      currentRoles = currentRoles.filter((currentRole) => currentRole !== role);

      if (role === EUserRoles.STAFF) {
        currentRoles = currentRoles.filter(
          (currentRole) => currentRole !== EUserRoles.ADMIN && currentRole !== EUserRoles.PORTFOLIO_MANAGER
        );
      }
    }

    const updatedRoles = USER_ROLE_VALUES.filter((userRole) => currentRoles.includes(userRole));

    updateStateAttribute<State>(
      {
        form: {
          ...state.form,
          roles: updatedRoles
        }
      },
      setState
    );
  };

  const handleDelete = async () => {
    if (!id) return;
    await UserManagementService.deleteAccount(id);
    dispatch(updateUserObject({}));
  };

  const sanitizedForm = {
    firstName: state.form.firstName?.trim() ?? '',
    lastName: state.form.lastName?.trim() ?? '',
    email: state.form.email?.trim() ?? '',
    role: state.form.roles ?? []
  };

  const isFormChanged =
    sanitizedForm.firstName !== initialForm.firstName ||
    sanitizedForm.lastName !== initialForm.lastName ||
    sanitizedForm.email !== initialForm.email ||
    !areRolesEqual(sanitizedForm.role, initialForm.roles);

  const isUpdateDisabled =
    !sanitizedForm.firstName || !sanitizedForm.lastName || !sanitizedForm.email || !isFormChanged;

  const updateHandler = async () => {
    if (isUpdateDisabled || !id) return;

    updateStateAttribute<State>(
      {
        reqState: { ...state.reqState, isLoading: true, isError: false }
      },
      setState
    );

    const res = await SvUserManagementCoreService.updateUserDetails(id, sanitizedForm);
    const isSuccess = await res.isSuccess();

    if (!isSuccess) {
      updateStateAttribute<State>(
        {
          reqState: {
            isLoading: false,
            isError: true,
            strError: await res.getMessage(),
            objError: await res.getDetail()
          }
        },
        setState
      );

      dispatch(
        updateSnackbar({
          message: await res.getMessage(),
          severity: 'error',
          isVisible: true
        })
      );
      return;
    }

    const updatedUser = await res.getData();

    updateStateAttribute<State>(
      {
        form: {
          firstName: updatedUser?.firstName ?? '',
          lastName: updatedUser?.lastName ?? '',
          email: updatedUser?.email ?? '',
          roles: USER_ROLE_VALUES.filter((role) => updatedUser?.roles?.includes(role) ?? false)
        },
        reqState: {
          isLoading: false,
          isError: false
        }
      },
      setState
    );

    dispatch(
      updateUserObject({
        id,
        email: updatedUser?.email ?? '',
        firstName: updatedUser?.firstName ?? '',
        lastName: updatedUser?.lastName ?? '',
        isAdmin: updatedUser?.roles?.includes(EUserRoles.ADMIN) ?? false,
        isUser:
          !updatedUser?.roles?.includes(EUserRoles.ADMIN) &&
          !updatedUser?.roles?.includes(EUserRoles.STAFF) &&
          !updatedUser?.roles?.includes(EUserRoles.PORTFOLIO_MANAGER),
        isStaff: updatedUser?.roles?.includes(EUserRoles.STAFF) ?? false,
        isPortfolioManager: updatedUser?.roles?.includes(EUserRoles.PORTFOLIO_MANAGER) ?? false,
        roles: updatedUser?.roles ?? []
      })
    );

    dispatch(
      updateSnackbar({
        message: await res.getMessage(),
        severity: 'success',
        isVisible: true
      })
    );
  };

  return (
    <Page title={accountManagementText}>
      <>
        <CPageModule>
          <h3>
            <CLocalizedText dictKey={'selfServiceAccount.page.module1'} />
          </h3>

          <PageModuleFormGroup>
            <PageModuleFormRow>
              <CustomTextField
                label={firstNameLabel}
                value={state.form.firstName ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, firstName: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>

            <PageModuleFormRow>
              <CustomTextField
                label={lastNameLabel}
                value={state.form.lastName ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, lastName: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>

            <PageModuleFormRow>
              <CustomTextField
                label={emailLabel}
                value={state.form.email ?? ''}
                onChange={(e) => {
                  updateStateAttribute<State>({ form: { ...state.form, email: e.target.value } }, setState);
                }}
              />
            </PageModuleFormRow>
            <PageModuleFormRow>
              <div>
                <p className={'mb-2'}>{CLocalizedTextStringStatic('userManagement.userRole.heading')}</p>
                <div className={'flex gap-4 flex-wrap'}>
                  {USER_ROLE_VALUES.map((role) => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={state.form.roles?.includes(role) ?? false}
                          onChange={(e) => {
                            handleRoleChange(role, e.target.checked);
                          }}
                        />
                      }
                      label={getRoleLabel(role)}
                    />
                  ))}
                </div>
              </div>
            </PageModuleFormRow>
            <PageModuleFormRow>
              <CButton
                style={{ borderWidth: 1, width: '100%', justifyContent: 'center' }}
                icons={{ start: <Save /> }}
                onClick={updateHandler}
                disabled={isUpdateDisabled}
                state={state.reqState}
              >
                <CLocalizedText dictKey={'update.btn.text'} />
              </CButton>
            </PageModuleFormRow>
          </PageModuleFormGroup>
        </CPageModule>
        {email && (
          <CPageModule>
            <h3>
              <CLocalizedText dictKey={'accountDelete.heading'} />
            </h3>
            <CAccountDeleteForm
              selfDelete={false} // This is an admin-initiated action
              emailAddress={email}
              onCancel={() => {}}
              onDelete={handleDelete}
            />
          </CPageModule>
        )}
      </>
    </Page>
  );
}
